import {rankBySearch} from '../../../../common/utils/searchRank';

export interface IFormulaMetric {
    value: string;
    label: string;
}

export interface IFormulaToken {
    /** Text typed so far for the current operand (metric names may contain spaces). */
    query: string;
    /** Range in the formula that a chosen metric replaces. */
    start: number;
    end: number;
}

export const MAX_FORMULA_SUGGESTIONS = 8;
const OPERATORS = /[+\-*/%()]/;

/** The operand around the caret: from the previous operator to the next one, without padding spaces. */
export function formulaTokenAt(text: string, caret: number): IFormulaToken {
    let start = caret;
    while (start > 0 && !OPERATORS.test(text[start - 1])) start--;
    while (start < caret && text[start] === ' ') start++;

    let end = caret;
    while (end < text.length && !OPERATORS.test(text[end])) end++;
    while (end > caret && text[end - 1] === ' ') end--;

    return {query: text.slice(start, caret), start, end};
}

/** Metrics that match what is being typed, most relevant first; none for numbers or an empty operand. */
export function formulaSuggestions(token: IFormulaToken, metrics: IFormulaMetric[]): IFormulaMetric[] {
    const query = token.query.trim().toLowerCase();
    if (!query || /^[\d.]+$/.test(query)) return [];
    const words = query.split(/\s+/);
    const matches = metrics.filter(metric => {
        const text = `${metric.label} ${metric.value}`.toLowerCase();
        return words.every(word => text.includes(word));
    });
    const ranked = rankBySearch(matches, query).slice(0, MAX_FORMULA_SUGGESTIONS);
    // Nothing left to complete once the operand already is the only matching name.
    if (ranked.length === 1 && ranked[0].label.toLowerCase() === query) return [];
    return ranked;
}

/** Replaces the operand with the metric's name and returns the caret position after it. */
export function applyFormulaSuggestion(text: string, token: IFormulaToken, metric: IFormulaMetric) {
    const value = text.slice(0, token.start) + metric.label + text.slice(token.end);
    return {value, caret: token.start + metric.label.length};
}
